'use client';

import clsx from 'clsx';
import { useEffect } from 'react';

import { MetricVersionConfigurationTab } from '@/app/evaluations/metric/_versionConfigurationTab/MetricVersionConfigurationTab';
import { Button } from '@/components/button/button';
import { Loader } from '@/components/loader/loader';
import Modal from '@/components/modal/modal';
import { Heading } from '@/components/typography/heading';
import { useMetricQuery } from '@/hooks/queries/useMetrics';
import { MetricConfig } from '@/types/evals';

import styles from './modalMetricShow.module.scss';

type Props = {
  onCloseAction: () => void;
  metricConfig: MetricConfig;
};

export default function ModalMetricShow( { onCloseAction, metricConfig }: Props ) {
  // Close the modal on Escape press to match common UX expectations
  useEffect( () => {
    const onKeyDown = ( e: KeyboardEvent ) => {
      if ( e.key === 'Escape' ) {
        onCloseAction();
      }
    };

    document.addEventListener( 'keydown', onKeyDown );
    return () => document.removeEventListener( 'keydown', onKeyDown );
  }, [ onCloseAction ] );

  // The version configuration tab needs the metric provider (for its
  // description and version list); the run results page only has the config, so
  // fetch the provider here.
  const metricQuery = useMetricQuery( metricConfig.metric_provider_id );
  const metric = metricQuery.data;

  return (
    <Modal
      resizable
      modalType="metric-details"
      initialSize="large"
      minSize={ { width: 500, height: 300 } }
    >
      <div className={ styles.content } data-testid='metric-details-modal'>
        <Heading as="h2" size="md" data-testid='metric-name-title'>
          Metric: { metricConfig.name }{ metricConfig.version != null ? ` (v${ metricConfig.version })` : '' }
        </Heading>
        <div className={ styles.body } data-testid='metric-details-content'>
          { metric ? (
            <MetricVersionConfigurationTab
              metric={ metric }
              config={ metricConfig }
              readOnly
            />
          ) : (
            <Loader />
          ) }
        </div>
        <div className={ clsx( 'layout__row', 'pad', 'flex_right' ) }>
          <Button variant='primary' onClick={ onCloseAction } data-testid='metric-details-close-button'>Close</Button>
        </div>
      </div>
    </Modal>
  );
}
